import React from "react";
import { connect } from "react-redux";
import { Badge, Button, Card, Col, Row } from "react-bootstrap";
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

import CsLineIcons from "../../../../cs-line-icons";
import BoardTaskModal from "../modals/board-task";
import { list as getBoardTasks, get as getBoardTask, save as saveBoardTask, move as moveBoardTask, destroy as deleteBoardTask } from "../../../../actions/boards";
import { get as getStaff } from "../../../../actions/staff";
import { loading } from "../../../../actions/page";
import { send as sendAlert } from "../../../../actions/alert";
import { format as formatDate } from "../../../../utils/date";

const DEFAULT_COLUMNS = [
    { key: "NOT_STARTED", label: "Not Started", terminal: false },
    { key: "STARTED", label: "Started", terminal: false },
    { key: "CANCELLED", label: "Cancelled", terminal: true },
    { key: "FINISHED", label: "Finished", terminal: true },
];

const labelForPerson = (person) => person?.displayName || [person?.firstName, person?.lastName].filter(Boolean).join(" ") || person?.email || "";

const BoardDndContext = ({ onDragEnd, children }) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 5,
            },
        })
    );

    return <DndContext sensors={sensors} onDragEnd={onDragEnd}>{children}</DndContext>;
};

const DroppableColumn = ({ column, children }) => {
    const { isOver, setNodeRef } = useDroppable({
        id: `board-status:${column.key}`,
        data: { status: column.key },
    });

    return (
        <Card className={`h-100 ${isOver ? "border-primary" : ""}`}>
            <Card.Body ref={setNodeRef} style={{ minHeight: 240 }}>
                {children}
            </Card.Body>
        </Card>
    );
};

const DraggableTask = ({ item, children }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: `board-task:${item.uuid}`,
        data: { item },
    });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Translate.toString(transform),
                opacity: isDragging ? 0.65 : 1,
                zIndex: isDragging ? 3 : "auto",
                position: "relative",
            }}
        >
            {children({ attributes, listeners, isDragging })}
        </div>
    );
};

class PersonBoard extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            items: [],
            columns: DEFAULT_COLUMNS,
            modal: {
                show: false,
                task: null,
                status: DEFAULT_COLUMNS[0].key,
            },
            staff: [],
        };
        this.openedInitialTaskUuid = null;
    }

    componentDidMount = async () => {
        await this.loadData();
        await this.openInitialTask();
    };

    componentDidUpdate = async (oldProps) => {
        if (oldProps.personUuid !== this.props.personUuid || oldProps.personType !== this.props.personType) {
            await this.loadData();
            this.openedInitialTaskUuid = null;
            await this.openInitialTask();
            return;
        }

        if (oldProps.initialTaskUuid !== this.props.initialTaskUuid) {
            this.openedInitialTaskUuid = null;
            await this.openInitialTask();
        }
    };

    loadData = async () => {
        if (!this.props.personUuid) return;
        this.props.loading(true);
        try {
            const [board, staff] = await Promise.all([
                this.props.getBoardTasks({ personUuid: this.props.personUuid, personType: this.props.personType, includeProfileTypes: true }),
                this.props.getStaff({}),
            ]);
            const items = Array.isArray(board?.items) ? board.items : [];
            this.setState({
                items,
                columns: Array.isArray(board?.columns) && board.columns.length ? board.columns : DEFAULT_COLUMNS,
                staff: Array.isArray(staff) ? staff : [],
            });
        } finally {
            this.props.loading(false);
        }
    };

    openTask = async (uuid) => {
        if (!uuid) return;
        this.props.loading(true);
        try {
            const task = await this.props.getBoardTask({ uuid });
            if (task?.success) {
                this.setState({
                    modal: {
                        show: true,
                        task,
                        status: task.status || this.state.columns[0]?.key || "NOT_STARTED",
                    },
                });
            }
        } finally {
            this.props.loading(false);
        }
    };

    openInitialTask = async () => {
        const uuid = this.props.initialTaskUuid;
        if (!uuid || this.openedInitialTaskUuid === uuid) return;

        this.openedInitialTaskUuid = uuid;
        await this.openTask(uuid);
    };

    onModalCancel = () => {
        this.setState({ modal: { show: false, task: null, status: this.state.columns[0]?.key || "NOT_STARTED" } });
    };

    onModalOk = async (result) => {
        const warning = result?.calendarWarning;
        this.onModalCancel();
        await this.loadData();
        this.props.sendAlert("Task saved.", "success");
        if (warning) this.props.sendAlert(warning, "warning");
    };

    onModalDelete = async () => {
        this.onModalCancel();
        await this.loadData();
        this.props.sendAlert("Task deleted.", "success");
    };

    onAddTask = (status) => {
        this.setState({
            modal: {
                show: true,
                task: null,
                status,
            },
        });
    };

    onDragEnd = async ({ active, over }) => {
        if (!active?.data?.current?.item || !over?.data?.current) return;
        const item = active.data.current.item;
        const status = over.data.current.status;
        const validTarget = this.columns.some((column) => column.key === status);
        const isSameStatus = String(item.status || "").toUpperCase() === String(status || "").toUpperCase();

        if (!validTarget || isSameStatus) return;

        const result = await this.props.moveBoardTask({
            uuid: item.uuid,
            personType: item.personType || this.props.personType,
            status,
            sortOrder: item.sortOrder || 0,
        });
        if (result?.success) await this.loadData();
    };

    get staffOptions() {
        return (this.state.staff || []).map((staff) => ({
            value: staff.uuid,
            label: `${labelForPerson(staff.Person)}${staff.title ? ` (${staff.title})` : ""}`.trim() || staff.uuid,
        }));
    }

    get columns() {
        return (this.state.columns || DEFAULT_COLUMNS).map((column) => ({
            ...column,
            items: (this.state.items || []).filter((item) => item.status === column.key),
        }));
    }

    renderTask = (item) => (
        <DraggableTask key={item.uuid} item={item}>
            {({ attributes, listeners, isDragging }) => (
                <button
                    type="button"
                    className={`w-100 text-start border rounded bg-white p-3 mb-2 ${isDragging ? "shadow" : ""}`}
                    onDoubleClick={() => this.openTask(item.uuid)}
                    {...attributes}
                    {...listeners}
                >
                    <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="fw-bold">{item.title || "Board task"}</div>
                    </div>
                    {item.description ? <div className="text-muted small mt-1">{item.description}</div> : null}
                    <div className="mt-2">
                        {item.priority ? <Badge bg={item.priority === "URGENT" ? "danger" : "light"} text={item.priority === "URGENT" ? undefined : "dark"} className="me-2">{item.priority}</Badge> : null}
                        {item.startBy ? <Badge bg="light" text="dark" className="me-2">Start {formatDate(item.startBy)}</Badge> : null}
                        {item.finishBy ? <Badge bg="light" text="dark" className="me-2">Finish {formatDate(item.finishBy)}</Badge> : null}
                        {item.AssignedTo ? <Badge bg="light" text="dark">{labelForPerson(item.AssignedTo)}</Badge> : null}
                    </div>
                </button>
            )}
        </DraggableTask>
    );

    render() {
        return (
            <>
                <BoardDndContext onDragEnd={this.onDragEnd}>
                    <Row className="g-3 flex-nowrap overflow-auto pb-3">
                        {this.columns.map((column) => (
                            <Col key={column.key} style={{ minWidth: 300, maxWidth: 360 }}>
                                <DroppableColumn column={column}>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h2 className="small-title mb-0">{column.label}</h2>
                                            {column.terminal ? <span className="text-small text-muted">Terminal</span> : null}
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <Badge bg="light" text="dark">{column.items.length}</Badge>
                                            <Button variant="outline-primary" size="sm" className="btn-icon btn-icon-only" onClick={() => this.onAddTask(column.key)}>
                                                <CsLineIcons icon="plus" />
                                            </Button>
                                        </div>
                                    </div>
                                    {column.items.length ? column.items.map(this.renderTask) : (
                                        <div className="text-muted small py-3">No tasks.</div>
                                    )}
                                </DroppableColumn>
                            </Col>
                        ))}
                    </Row>
                </BoardDndContext>
                <BoardTaskModal
                    show={this.state.modal.show}
                    task={this.state.modal.task}
                    status={this.state.modal.status}
                    personUuid={this.props.personUuid}
                    personType={this.props.personType}
                    columns={this.state.columns}
                    staffOptions={this.staffOptions}
                    saveTask={this.props.saveBoardTask}
                    deleteTask={this.props.deleteBoardTask}
                    onOk={this.onModalOk}
                    onDelete={this.onModalDelete}
                    onCancel={this.onModalCancel}
                />
            </>
        );
    }
}

export default connect(null, {
    getBoardTasks,
    getBoardTask,
    saveBoardTask,
    moveBoardTask,
    deleteBoardTask,
    getStaff,
    loading,
    sendAlert,
}, null, { forwardRef: true })(PersonBoard);
